<template>
  <div>
    <loading-overlay v-if="loadingEventNews" :text="''" />
    <v-container fluid class="px-6 py-6">
      <!-- Permission Check: Nur für Admins und Manager (KEIN POS) -->
      <permission-denied 
        v-if="!hasAdminOrManagerPermission" 
        message="Du benötigst Manager- oder Administrator-Rechte, um News zu verwalten."
      />

      <!-- News Content nur für berechtigte User -->
      <div v-else>
      <!-- Year Selection - Sticky under navbar -->
      <year-selector :sticky="true" />
      <v-row>
        <v-col cols="12" md="6">
          <v-btn
            elevation="0"
            height="43"
            class="
              font-weight-bold
              text-uppercase
              btn-default
              bg-gradient-primary
              py-2
              px-6
              me-2
              mb-sm-0 mb-2
            "
            color="#5e72e4"
            small
            @click="$router.push('/admin/news/new').catch(() => {})"
            >Neue News anlegen</v-btn
          >
        </v-col>
        <v-col cols="12" md="6" class="text-right">
          <v-switch
            v-model="showArchived"
            label="Archivierte anzeigen"
            color="primary"
            :style="showArchived ? {} : { marginTop: '16px' }"
            @change="loadNews"
          ></v-switch>
        </v-col>
      </v-row>
      
      <!-- Keine News Meldung -->
      <v-row v-if="eventNews.length === 0 && !loadingEventNews" class="mb-6 mt-10">
        <v-col cols="12">
          <v-card class="card-shadow border-radius-xl py-10 text-center">
            <div class="mx-auto mb-4">
              <v-avatar
                color="bg-gradient-secondary"
                size="80"
                class="shadow"
              >
                <i class="material-icons-round text-white" style="font-size: 40px;">article</i>
              </v-avatar>
            </div>
            <h4 class="text-h5 text-typo font-weight-bolder mb-3">
              Keine News gefunden
            </h4>
            <p class="text-body font-weight-light mb-4 px-6">
              Für das Jahr {{ selectedYear || currentYear }} sind keine News vorhanden.
              <span v-if="!showArchived"><br>Archivierte News sind ausgeblendet.</span>
            </p>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-6 mt-10" v-else>
        <v-col
          v-for="(item, i) in eventNews"
          :key="item.title + i"
          lg="4"
          class="pt-0 mb-10"
        >
          <v-card
            class="card card-shadow border-radius-xl py-5 text-center"
            data-animation="true"
          >
            <div class="mt-n11 mx-4 card-header position-relative z-index-2">
              <div class="d-block blur-shadow-image">
                <img
                  v-if="!item.imageError && item.img"
                  :src="getImage(item.img)"
                  class="img-fluid shadow border-radius-lg"
                  :alt="item.image"
                  @error="handleImageError(item)"
                  style="height: 250px; object-fit: cover;"
                />
                <div
                  v-else
                  class="d-flex align-center justify-center border-radius-lg"
                  style="
                    height: 250px;
                    background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
                  "
                >
                  <i class="material-icons-round" style="font-size: 80px; color: white; opacity: 0.5;">
                    image_not_supported
                  </i>
                </div>
              </div>
              <div
                v-if="!item.imageError"
                class="colored-shadow"
                v-bind:style="{
                  backgroundImage: 'url(' + getImage(item.img) + ')',
                }"
              ></div>
            </div>
            <div class="d-flex mx-auto mt-n8">
              <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        @click="
                          $router
                            .push('/admin/news/details/' + item._id)
                            .catch(() => {})
                        "
                        class="material-icons-round text-primary ms-auto px-5"
                        size="18"
                      >
                        info
                      </v-icon>
                    </template>
                    <span>Details</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        @click="
                          $router
                            .push('/admin/news/edit/' + item._id)
                            .catch(() => {})
                        "
                        v-bind="attrs"
                        v-on="on"
                        class="material-icons-round text-info me-auto px-5"
                        size="18"
                      >
                        edit
                      </v-icon>
                    </template>
                    <span>Bearbeiten</span>
                  </v-tooltip>
            </div>
            <h5 class="font-weight-normal text-typo text-h5 mt-7 mb-2 px-4">
              <a
                href="javascript:;"
                class="text-decoration-none text-default"
                >{{ item.title }}</a
              >
            </h5>
            <p class="mb-0 text-body font-weight-light px-5">
              {{ item.text }}
            </p>
            <hr class="horizontal dark my-6" />
            
            <!-- Archived Badge or Active Switch -->
            <div v-if="item.archived" class="mx-4 px-4 mb-4">
              <v-chip color="grey" dark small>
                <i class="material-icons-round mr-1" style="font-size: 16px;">inventory</i>
                Archiviert
              </v-chip>
            </div>
            <div v-else class="d-flex align-center mx-4 px-4 mb-4">
              <p class="mb-0 text-sm text-body font-weight-normal">Aktiv</p>
              <v-switch
                v-model="item.active"
                color="success"
                class="ml-auto mb-0"
                @change="updateNews(item)"
              ></v-switch>
            </div>
            
            <div class="d-flex text-body mx-2 px-4">
              <p class="mb-0 font-weight-normal text-body">
                Beitragsinteraktionen:
              </p>
              <i
                class="
                  material-icons-round
                  position-relative
                  ms-auto
                  text-lg
                  me-1
                  my-auto
                "
                >person</i
              >
              <p class="text-sm my-auto font-weight-light" v-if="item.views">
                {{ item.views }}
              </p>
              <p class="text-sm my-auto font-weight-light" v-else>0</p>
            </div>
          </v-card>
        </v-col>
      </v-row>
      </div><!-- End: News Content -->
    </v-container>
  </div>
</template>
<script>
// import CalendarWidget from "./Widgets/CalendarWidget.vue";
import PermissionDenied from "@/components/PermissionDenied.vue";
import permissionMixin from "@/mixins/permissionMixin";
import administrationData from "@/mixins/administrationMixin";
import YearSelector from "@/components/YearSelector.vue";

export default {
  name: "News",
  mixins: [permissionMixin, administrationData],
  components: {
    // CalendarWidget,
    PermissionDenied,
    YearSelector,
  },
  data: function () {
    return {
      showArchived: false,
    };
  },
  mounted() {
    // Initialize with current year
    const thisYear = new Date().getFullYear();
    if (!this.selectedYear) {
      this.$store.commit('selectedYear', thisYear);
    }
    
    this.loadNews();
  },
  computed: {
    maxSlots() {
      var max = 0;
      if (this.newEvent.sponsors.length > 0) {
        this.newEvent.sponsors.forEach((sponsor) => {
          max += parseInt(sponsor.slots);
        });
      }
      return max;
    },
    loggedIn() {
      return this.$store.getters.loggedIn;
    },
    darkMode() {
      return this.$store.getters.darkMode;
    },
    eventEntries() {
      var counter = 0;
      if (this.events) {
        this.events.forEach((event) => {
          if (event.entries) {
            counter += event.entries.length;
          }
        });
      }

      return counter;
    },
    contactFormSent() {
      return this.$store.getters.contactFormSent;
    },
    events() {
      return this.$store.getters.events;
    },
    eventNews() {
      return this.$store.getters.eventNews;
    },
    loadingEventNews() {
      return this.$store.getters.loadingEventNews;
    },
    interactions(){
      var counter = 0
      this.events.forEach(event => {
        counter += event.views
      })
      this.eventNews.forEach(news => {
        counter += news.views
      })
      return counter;
    },
    updating() {
      return this.$store.getters.updating;
    },
    authError() {
      return this.$store.getters.authError;
    },
    authMessage() {
      return this.$store.getters.authMessage;
    },
    emailResended() {
      return this.$store.getters.emailResended;
    },
    filteredEntries() {
      var preFiltered = [];
      var endFiltered = [];
      if (this.eventBuffer) {
        preFiltered = this.eventEntries.filter(
          (el) => el.event == this.eventBuffer._id
        );
        endFiltered = preFiltered.filter((el) => this.checkFilter(el));
        return endFiltered;
      }
      return [];
    },
  },
  watch: {
    selectedYear() {
      // Reload news when year changes
      this.loadNews();
    },
  },
  methods: {
    loadNews() {
      this.$store.dispatch("getEventNewsAdmin", { 
        includeArchived: this.showArchived,
        year: this.selectedYear || this.currentYear
      });
    },
    updateNews(news) {
      this.$store.dispatch("updateEventNews", news);
    },
    getImage(link) {
      if (!link) return null;
      return process.env.VUE_APP_API_BASEURL + "/" + link;
    },
    handleImageError(item) {
      this.$set(item, 'imageError', true);
    },
  },
};
</script>
